import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/common/SEO';

export default function PlanTrip() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://la-antigua-guatemala.com';
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    travelDates: '',
    numberOfTravelers: '',
    budgetRange: '',
    interests: [] as string[],
    accommodationPreference: '',
    needAirportTransfer: '',
    additionalNotes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (interest: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(interest)
        ? prev.interests.filter(i => i !== interest)
        : [...prev.interests, interest]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('travelDates', formData.travelDates);
      formBody.append('numberOfTravelers', formData.numberOfTravelers);
      formBody.append('budgetRange', formData.budgetRange);
      formBody.append('interests', formData.interests.join(', '));
      formBody.append('accommodationPreference', formData.accommodationPreference);
      formBody.append('needAirportTransfer', formData.needAirportTransfer);
      formBody.append('additionalNotes', formData.additionalNotes);

      const response = await fetch('https://readdy.ai/api/form/d6bun4aohb161tfd7pv0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          travelDates: '',
          numberOfTravelers: '',
          budgetRange: '',
          interests: [],
          accommodationPreference: '',
          needAirportTransfer: '',
          additionalNotes: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Plan My Antigua Guatemala Trip | Custom Itinerary Help"
        description="Get personalized recommendations for your Antigua itinerary, hotels, volcano hikes, and transportation"
        canonical={`${siteUrl}/plan-my-antigua-trip`}
        ogImage={{
          url: "https://public.readdy.ai/ai/img_res/12ac47ed8c270e0374295f1313e7fa1f.jpg",
          width: 1200,
          height: 630
        }}
        structuredData={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "@id": `${siteUrl}/plan-my-antigua-trip`,
            "name": "Plan My Antigua Guatemala Trip | Custom Itinerary Help",
            "description": "Get personalized recommendations for your Antigua itinerary, hotels, volcano hikes, and transportation.",
            "url": `${siteUrl}/plan-my-antigua-trip`,
            "inLanguage": "en-US",
            "isPartOf": { "@type": "WebSite", "url": `${siteUrl}/` }
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` },
              { "@type": "ListItem", "position": 2, "name": "Plan My Trip", "item": `${siteUrl}/plan-my-antigua-trip` }
            ]
          }
        ]}
      />
      <Navbar darkText />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-teal-50 to-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Plan My Antigua Guatemala Trip
          </h1>
          <p className="text-xl md:text-2xl text-gray-700 mb-8">
            Get personalized recommendations for your Antigua itinerary, hotels, volcano hikes, and transportation.
          </p>
          <a 
            href="#planning-form" 
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white px-10 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
          >
            Get My Custom Plan
          </a>
          <div className="flex flex-wrap justify-center gap-6 mt-8 text-gray-700">
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-teal-500 text-xl"></i>
              <span className="font-medium">US traveler friendly</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-teal-500 text-xl"></i>
              <span className="font-medium">Local expertise</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-check-line text-teal-500 text-xl"></i>
              <span className="font-medium">Free recommendations</span>
            </div>
          </div>
        </div>
      </section>

      {/* Intro Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-lg text-gray-700 leading-relaxed">
            Plan your Antigua Guatemala trip with confidence. Whether you're looking for the perfect itinerary, need help booking airport transfers, want to hike Acatenango volcano, or need hotel recommendations by neighborhood, we provide personalized planning assistance for US travelers. Share your travel dates and preferences below, and we'll create a custom plan including day trips to Chichicastenango, Lake Atitlán, and other must-see destinations around Antigua.
          </p>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">How It Works</h2>
          <p className="text-center text-gray-600 mb-12">No payment required. No obligation.</p>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-500">1</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Tell Us Your Travel Dates</h3>
              <p className="text-gray-600">Share when you're planning to visit Antigua and how long you'll stay.</p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-500">2</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Share Your Preferences</h3>
              <p className="text-gray-600">Let us know your budget, interests, and hiking level so we can tailor recommendations.</p>
            </div>

            <div className="bg-white p-8 rounded-lg shadow-sm text-center">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold text-teal-500">3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">Receive Personalized Recommendations</h3>
              <p className="text-gray-600">Get a custom plan with itinerary, hotels, tours, and transportation options.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section id="planning-form" className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Antigua%20Guatemala%20colonial%20city%20with%20colorful%20terracotta%20rooftops%20cobblestone%20streets%20surrounded%20by%20lush%20green%20volcanoes%20and%20mountains%20golden%20hour%20warm%20light%20beautiful%20travel%20destination%20Central%20America%20panoramic%20landscape&width=1920&height=1200&seq=form-bg-001&orientation=landscape"
            alt="Antigua Guatemala panoramic view"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-gray-900/80 via-gray-900/70 to-teal-900/60"></div>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-72 h-72 bg-teal-500/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-teal-400/10 rounded-full blur-3xl translate-x-1/3 translate-y-1/3"></div>

        <div className="relative max-w-3xl mx-auto px-4">
          {/* Section Header */}
          <div className="text-center mb-10">
            <span className="inline-block bg-teal-500/20 text-teal-300 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 backdrop-blur-sm border border-teal-400/20">
              Free & No Obligation
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Start Planning Your Trip</h2>
            <p className="text-lg text-gray-300 max-w-xl mx-auto">Share your preferences and we&apos;ll craft a personalized Antigua itinerary just for you.</p>
          </div>

          {/* Form Card */}
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 md:p-12 shadow-2xl shadow-black/20 border border-white/30">
            
            <form onSubmit={handleSubmit} data-readdy-form id="plan-trip-form">
              <div className="space-y-6">
                {/* Full Name & Email Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-semibold text-gray-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                {/* Travel Dates & Travelers Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="travelDates" className="block text-sm font-semibold text-gray-900 mb-2">
                      Travel Dates *
                    </label>
                    <input
                      type="text"
                      id="travelDates"
                      name="travelDates"
                      value={formData.travelDates}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition"
                      placeholder="e.g., March 15-20, 2025"
                    />
                  </div>
                  <div>
                    <label htmlFor="numberOfTravelers" className="block text-sm font-semibold text-gray-900 mb-2">
                      Number of Travelers *
                    </label>
                    <select
                      id="numberOfTravelers"
                      name="numberOfTravelers"
                      value={formData.numberOfTravelers}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition cursor-pointer"
                    >
                      <option value="">Select number</option>
                      <option value="1">Solo traveler</option>
                      <option value="2">2 travelers</option>
                      <option value="3-4">3-4 travelers</option>
                      <option value="5+">5+ travelers</option>
                    </select>
                  </div>
                </div>

                {/* Budget Range */}
                <div>
                  <label htmlFor="budgetRange" className="block text-sm font-semibold text-gray-900 mb-2">
                    Budget Range *
                  </label>
                  <select
                    id="budgetRange"
                    name="budgetRange"
                    value={formData.budgetRange}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition cursor-pointer"
                  >
                    <option value="">Select budget</option>
                    <option value="budget">Budget ($50-100/day)</option>
                    <option value="mid-range">Mid-range ($100-200/day)</option>
                    <option value="upscale">Upscale ($200-350/day)</option>
                    <option value="luxury">Luxury ($350+/day)</option>
                  </select>
                </div>

                {/* Interests */}
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-3">
                    Interests (select all that apply)
                  </label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { label: 'Volcano Hikes', icon: 'ri-fire-line' },
                      { label: 'Colonial Culture', icon: 'ri-ancient-gate-line' },
                      { label: 'Coffee Tours', icon: 'ri-cup-line' },
                      { label: 'Day Trips', icon: 'ri-compass-3-line' },
                      { label: 'Relaxing / Boutique Hotels', icon: 'ri-hotel-line' }
                    ].map((interest) => (
                      <label
                        key={interest.label}
                        className={`flex items-center gap-2.5 px-4 py-3 rounded-lg border-2 cursor-pointer transition ${
                          formData.interests.includes(interest.label)
                            ? 'border-teal-500 bg-teal-50 text-teal-700'
                            : 'border-gray-200 bg-gray-50/50 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(interest.label)}
                          onChange={() => handleCheckboxChange(interest.label)}
                          className="hidden"
                        />
                        <i className={`${interest.icon} text-lg`}></i>
                        <span className="text-sm font-medium">{interest.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Accommodation & Airport Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="accommodationPreference" className="block text-sm font-semibold text-gray-900 mb-2">
                      Accommodation Preference
                    </label>
                    <select
                      id="accommodationPreference"
                      name="accommodationPreference"
                      value={formData.accommodationPreference}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition cursor-pointer"
                    >
                      <option value="">Select preference</option>
                      <option value="hostel">Hostel</option>
                      <option value="budget-hotel">Budget Hotel</option>
                      <option value="boutique-hotel">Boutique Hotel</option>
                      <option value="luxury-hotel">Luxury Hotel</option>
                      <option value="airbnb">Airbnb/Vacation Rental</option>
                    </select>
                  </div>
                  <div>
                    <label htmlFor="needAirportTransfer" className="block text-sm font-semibold text-gray-900 mb-2">
                      Need Airport Transfer?
                    </label>
                    <select
                      id="needAirportTransfer"
                      name="needAirportTransfer"
                      value={formData.needAirportTransfer}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm bg-gray-50/50 transition cursor-pointer"
                    >
                      <option value="">Select option</option>
                      <option value="yes">Yes, I need airport transfer</option>
                      <option value="no">No, I have transportation arranged</option>
                    </select>
                  </div>
                </div>

                {/* Additional Notes */}
                <div>
                  <label htmlFor="additionalNotes" className="block text-sm font-semibold text-gray-900 mb-2">
                    Additional Notes
                  </label>
                  <textarea
                    id="additionalNotes"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none bg-gray-50/50 transition"
                    placeholder="Any specific questions or requirements? (max 500 characters)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.additionalNotes.length}/500 characters</p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 disabled:from-gray-400 disabled:to-gray-400 text-white px-8 py-4 rounded-lg font-semibold text-lg transition shadow-lg shadow-teal-500/25 whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Get My Personalized Plan'}
                </button>

                <p className="text-center text-sm text-gray-500">
                  <i className="ri-time-line mr-1"></i>
                  We typically respond within 24 hours.
                </p>

                {/* Status Messages */}
                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                    <strong>Success!</strong> Your trip planning request has been submitted. We&apos;ll send personalized recommendations to your email within 24 hours.
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                    <strong>Error:</strong> Something went wrong. Please try again or contact us directly.
                  </div>
                )}
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* What We Can Help With */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">What We Can Help With</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/itineraries" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-calendar-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Custom Antigua Itinerary</h3>
              <p className="text-gray-600 text-sm">Personalized 2-5 day itineraries based on your interests and travel style.</p>
            </Link>

            <Link to="/acatenango-hike-from-antigua" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-mountain-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Acatenango & Pacaya Planning</h3>
              <p className="text-gray-600 text-sm">Expert advice on volcano hikes, tour operators, and what to pack.</p>
            </Link>

            <Link to="/guatemala-city-airport-to-antigua" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-bus-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Shuttle & Airport Transfers</h3>
              <p className="text-gray-600 text-sm">Safe, reliable transportation options from Guatemala City airport.</p>
            </Link>

            <Link to="/where-to-stay-in-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-hotel-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Best Hotels by Neighborhood</h3>
              <p className="text-gray-600 text-sm">Accommodation recommendations matching your budget and preferences.</p>
            </Link>

            <Link to="/day-trips-from-antigua-guatemala" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-compass-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Day Trip Planning</h3>
              <p className="text-gray-600 text-sm">Chichicastenango market, Lake Atitlán, Iximché ruins, and more.</p>
            </Link>

            <Link to="/is-antigua-guatemala-safe" className="bg-white p-6 rounded-lg shadow-sm hover:shadow-md transition cursor-pointer group">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-teal-500 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-teal-500 transition">Safety & Budget Advice</h3>
              <p className="text-gray-600 text-sm">Practical tips for US travelers on safety, costs, and local customs.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Sample Mini-Plan */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-4 text-center">Sample 3-Day Antigua Trip</h2>
          <p className="text-center text-gray-600 mb-12">Here's an example of what your personalized plan might look like:</p>
          
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-teal-50 to-white p-8 rounded-lg border-l-4 border-teal-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Day 1 – Colonial Center</h3>
                  <p className="text-gray-700">Explore Parque Central, Santa Catalina Arch, La Merced Church, and local markets. Evening coffee tasting at a rooftop café.</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-white p-8 rounded-lg border-l-4 border-teal-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Day 2 – Acatenango Hike</h3>
                  <p className="text-gray-700">Overnight volcano trek with sunset and sunrise views of erupting Fuego. Camp at 3,700m with experienced guides.</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-50 to-white p-8 rounded-lg border-l-4 border-teal-500">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Day 3 – Coffee Farm & Relax</h3>
                  <p className="text-gray-700">Morning coffee plantation tour, afternoon spa treatment or boutique shopping, farewell dinner at a colonial restaurant.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Trust Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Why Trust Us</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-focus-line text-teal-500 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Antigua Specialists</h3>
                <p className="text-gray-600">We focus exclusively on Antigua Guatemala, providing deep local knowledge and up-to-date recommendations.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-user-location-line text-teal-500 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">US Traveler Focused</h3>
                <p className="text-gray-600">All our content and recommendations are tailored specifically for American travelers visiting Guatemala.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-line text-teal-500 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Clear Editorial Policy</h3>
                <p className="text-gray-600">We maintain transparency in our recommendations and partnerships. <Link to="/editorial-policy" className="text-teal-500 hover:text-teal-600 cursor-pointer">Read our policy</Link>.</p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <i className="ri-map-2-line text-teal-500 text-2xl"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Local Knowledge</h3>
                <p className="text-gray-600">Our team has extensive on-the-ground experience in Antigua and throughout Guatemala. <Link to="/about" className="text-teal-500 hover:text-teal-600 cursor-pointer">Learn more</Link>.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Frequently Asked Questions</h2>
          
          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Is this service free?</h3>
              <p className="text-gray-700">Yes, our trip planning recommendations are completely free. We may suggest tours, hotels, and transportation options, but you're never obligated to book through us.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Do you book tours directly?</h3>
              <p className="text-gray-700">We provide recommendations and can connect you with trusted tour operators, but you'll book directly with them. This ensures you get the best prices and customer service.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How long should I stay in Antigua?</h3>
              <p className="text-gray-700">We recommend at least 3 days to experience the colonial center and one volcano hike. Five days allows for multiple day trips and a more relaxed pace. Check our <Link to="/itineraries" className="text-teal-500 hover:text-teal-600 cursor-pointer">itineraries page</Link> for detailed options.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Is Antigua Guatemala safe?</h3>
              <p className="text-gray-700">Yes, Antigua is one of Guatemala's safest cities and very popular with US travelers. We provide specific safety tips in our <Link to="/is-antigua-guatemala-safe" className="text-teal-500 hover:text-teal-600 cursor-pointer">safety guide</Link>.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">What airport do I fly into?</h3>
              <p className="text-gray-700">Fly into Guatemala City's La Aurora International Airport (GUA). Antigua is just 45 minutes away by shuttle. See our <Link to="/guatemala-city-airport-to-antigua" className="text-teal-500 hover:text-teal-600 cursor-pointer">airport transfer guide</Link> for details.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">Do you help with Acatenango tours?</h3>
              <p className="text-gray-700">Absolutely! We provide detailed information about Acatenango volcano hikes, including tour operator recommendations, packing lists, and what to expect. Visit our <Link to="/acatenango-hike-from-antigua" className="text-teal-500 hover:text-teal-600 cursor-pointer">Acatenango guide</Link>.</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">How far is Antigua from Guatemala City?</h3>
              <p className="text-gray-700">Antigua is approximately 45 minutes from Guatemala City airport by shuttle or private transfer. The distance is about 45 kilometers (28 miles).</p>
            </div>

            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-3">When will I receive my personalized plan?</h3>
              <p className="text-gray-700">We typically respond within 24 hours with customized recommendations based on your travel dates, budget, and interests.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-gradient-to-br from-teal-500 to-teal-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Ready to Plan Your Antigua Adventure?</h2>
          <p className="text-xl text-white/90 mb-8">Get personalized recommendations tailored to your travel style and preferences.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="#planning-form" 
              className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-10 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
            >
              Start Planning Now
            </a>
            <Link 
              to="/plan-my-antigua-trip"
              className="inline-block bg-white hover:bg-gray-100 text-teal-500 px-8 py-4 rounded-lg font-semibold text-lg transition whitespace-nowrap cursor-pointer"
            >
              Download Free Itinerary
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}